import { useEffect, useRef } from 'react';
import { buildDraftScopeKey, captureFieldFromElement } from '../utils/dialogDraftStore';

/**
 * M004-F0613 / T-M004-0020 — shared draft-capture primitive for "New X" / "Edit X"
 * dialogs. See docs/design/M004-D016-Dialog-and-Modal-Interaction-Standard.md §5, §6
 * (studioaeye-Core) for the normative behaviour; this hook is the one place that
 * implements it so per-dialog hand-rolled caching doesn't reappear at each call site.
 *
 * Usage: attach `containerRef` to the dialog's box element (the panel that stops click
 * propagation, not the overlay backdrop). While the dialog is mounted and `active`, every
 * `input`/`change` event bubbling up from a field inside the box is captured into the
 * in-memory draft store for `{dialogId}:{recordId|"new"}` — automatically, with no
 * per-field wiring, so a field added to the dialog later is covered for free and a
 * sensitive field (checked by src/utils/dialogDraftStore's isSensitiveField) is excluded
 * for free.
 *
 * This hook only captures. Reading a draft back to seed a dialog's initial state, and
 * clearing a draft on successful submit, are the caller's job (via the pure
 * getDraftForScope/clearDraftForScope functions in dialogDraftStore) because both of
 * those happen at moments — opening the dialog, handling a submit response — that are
 * specific to each component and cannot be generically hooked here.
 *
 * @param {string} dialogId - stable identifier for this dialog type/mode, e.g. "admin-user-edit".
 * @param {string|number|null|undefined} recordId - the record being edited, or omitted/"new" for a create dialog.
 * @param {{active?: boolean}} [options] - active defaults to true; pass false to disable capture (e.g. while closed).
 * @returns {{ containerRef: React.MutableRefObject, scopeKey: string }}
 */
export function useDialogDraft(dialogId, recordId, { active = true } = {}) {
  const containerRef = useRef(null);
  const scopeKey = buildDraftScopeKey(dialogId, recordId);

  useEffect(() => {
    if (!active) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const handleFieldEvent = (event) => {
      captureFieldFromElement(scopeKey, event.target);
    };

    // Capture phase + delegated on the box element: covers every current and future
    // field inside the dialog without per-field onChange edits, and 'input' fires on
    // every keystroke (not just on blur/submit like 'change' does for text fields).
    el.addEventListener('input', handleFieldEvent, true);
    el.addEventListener('change', handleFieldEvent, true);

    return () => {
      el.removeEventListener('input', handleFieldEvent, true);
      el.removeEventListener('change', handleFieldEvent, true);
    };
  }, [scopeKey, active]);

  return { containerRef, scopeKey };
}
